import React, { useState, useRef, useEffect } from 'react';

const ImageBlurEditor = ({ imageUrl, onSave, onCancel }) => {
    const [rects, setRects] = useState([]);
    const [isDrawing, setIsDrawing] = useState(false);
    const [startPos, setStartPos] = useState({ x: 0, y: 0 });
    const canvasRef = useRef(null);
    const imageRef = useRef(new Image());
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        imageRef.current.src = imageUrl;
        imageRef.current.onload = () => {
            const canvas = canvasRef.current;
            const maxWidth = 800;
            let width = imageRef.current.width;
            let height = imageRef.current.height;

            if (width > maxWidth) {
                height = (maxWidth * height) / width;
                width = maxWidth;
            }

            canvas.width = width;
            canvas.height = height;
            setLoading(false);
            draw();
        };
    }, [imageUrl]);

    const draw = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        
        // Clear and draw base image
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height);

        // Draw individual blurs
        rects.forEach(rect => {
            ctx.save();
            
            // Create a clipping region for the blur
            ctx.beginPath();
            ctx.rect(rect.x, rect.y, rect.w, rect.h);
            ctx.clip();

            // Draw blurred version of the image
            ctx.filter = 'blur(15px)';
            ctx.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height);
            
            // Optional: Draw a dark overlay to make it completely unreadable
            ctx.filter = 'none';
            ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
            ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
            
            ctx.restore();

            // Border for identifying the area while editing
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
            ctx.lineWidth = 1;
            ctx.setLineDash([5, 5]);
            ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);
        });
    };

    useEffect(() => {
        if (!loading) draw();
    }, [rects, loading]);

    const handleMouseDown = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        const scX = canvasRef.current.width / rect.width;
        const scY = canvasRef.current.height / rect.height;
        const x = (e.clientX - rect.left) * scX;
        const y = (e.clientY - rect.top) * scY;
        
        setIsDrawing(true);
        setStartPos({ x, y });
        setRects([...rects, { x, y, w: 0, h: 0 }]);
    };

    const handleMouseMove = (e) => {
        if (!isDrawing) return;
        
        const rect = canvasRef.current.getBoundingClientRect();
        const scX = canvasRef.current.width / rect.width;
        const scY = canvasRef.current.height / rect.height;
        const x = (e.clientX - rect.left) * scX;
        const y = (e.clientY - rect.top) * scY;
        
        const newRects = [...rects];
        const lastRect = newRects[newRects.length - 1];
        lastRect.w = x - startPos.x;
        lastRect.h = y - startPos.y;
        
        setRects(newRects);
    };

    const handleMouseUp = () => {
        setIsDrawing(false);
        // Remove tiny rects
        setRects(rects.filter(r => Math.abs(r.w) > 5 && Math.abs(r.h) > 5));
    };

    const handleReset = () => {
        setRects([]);
    };

    const handleSave = () => {
        // Draw one final time without borders and dashes
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height);

        rects.forEach(rect => {
            ctx.save();
            ctx.beginPath();
            ctx.rect(rect.x, rect.y, rect.w, rect.h);
            ctx.clip();
            ctx.filter = 'blur(15px)';
            ctx.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height);
            
            // Solid gray overlay for better visual result and guaranteed privacy
            ctx.filter = 'none';
            ctx.fillStyle = '#6c757d'; // AdminLTE secondary gray
            ctx.globalAlpha = 0.7; // Slight transparency to keep it looking "blurred"
            ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
            ctx.globalAlpha = 1.0;
            ctx.restore();
        });

        const dataUrl = canvas.toDataURL('image/png');
        onSave(dataUrl);
    };

    return (
        <div className="image-blur-editor">
            <div className="d-flex flex-column align-items-center bg-dark p-4 rounded shadow-lg overflow-hidden" style={{ minHeight: '400px' }}>
                {loading && <div className="spinner-border text-light m-5"></div>}
                
                <div style={{ position: 'relative', cursor: 'crosshair', display: loading ? 'none' : 'block' }}>
                    <canvas 
                        ref={canvasRef}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        className="rounded border border-secondary"
                    />
                </div>
                
                {!loading && (
                    <div className="mt-4 w-100 d-flex justify-content-between align-items-center px-2">
                        <div className="text-light small">
                            <i className="fas fa-mouse mr-2"></i> Glissez pour flouter une zone ({rects.length} zones)
                        </div>
                        <div className="d-flex gap-2">
                            <button className="btn btn-outline-light btn-sm mr-2" onClick={handleReset}>
                                <i className="fas fa-undo mr-1"></i> Réinitialiser
                            </button>
                            <button className="btn btn-secondary btn-sm" onClick={onCancel}>
                                Annuler
                            </button>
                            <button className="btn btn-primary btn-sm px-4 ml-2 font-weight-bold" onClick={handleSave}>
                                <i className="fas fa-save mr-1"></i> Appliquer & Fermer
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImageBlurEditor;
